import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { buildTheme } from './theme';

type Mode = 'dark' | 'light';

interface ThemeModeContextValue {
  mode: Mode;
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

interface AppThemeProviderProps {
  children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps): JSX.Element {
  // Default dark, consistente con el resto del portfolio. Persistimos en localStorage solo la
  // preferencia visual (NO el auth token — eso queda in-memory).
  const [mode, setMode] = useState<Mode>(() => {
    if (typeof window === 'undefined') return 'dark';
    const saved = window.localStorage.getItem('afip-admin-theme');
    return saved === 'light' ? 'light' : 'dark';
  });

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('afip-admin-theme', next);
      }
      return next;
    });
  }, []);

  // Sincronizar el atributo `data-theme` en <html> para que las custom properties del
  // design system (tilde-tokens.css) — usadas por marketing y componentes custom — sigan
  // el mismo modo que el theme MUI. La clase `theming` congela transiciones 1 frame para
  // evitar el flash de recompute de variables al cambiar de tema (ver tilde-tokens.css).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.add('theming');
    root.setAttribute('data-theme', mode);
    const id = window.setTimeout(() => root.classList.remove('theming'), 60);
    return () => window.clearTimeout(id);
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const ctxValue = useMemo<ThemeModeContextValue>(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ThemeModeContext.Provider value={ctxValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within AppThemeProvider.');
  }
  return ctx;
}
